import { CascadeDialog } from '@/components/admin/versioned/cascade-dialog';
import { clockTime, devCompareNote, isFailing, prodReadNote, syncBox } from '@/lib/atelier-status';
import { CRITERION_WORDING } from '@/lib/criterion-wording';
import { EXPERIENCE_WORDING } from '@/lib/experience-wording';
import { METHOD_WORDING } from '@/lib/method-wording';
import { MODEL_SETTINGS_WORDING } from '@/lib/model-settings-wording';
import { NOYAU_TEXTS_WORDING } from '@/lib/noyau-texts-wording';
import { PERSONA_WORDING } from '@/lib/persona-wording';
import {
  EMPTY_ANOMALIES,
  KIND_EYEBROW,
  NO_PROD_SENTENCE,
  SCREEN_LEAD,
  SCREEN_TITLE,
  type Verdict,
  type VerdictBadge,
  anomalyReason,
  dayAndHour,
  editorTarget,
  prettyContent,
  productionNote,
  unreachableNote,
  verdictOf,
} from '@/lib/prod-state-rows';
import { checkPromotion } from '@/lib/promotion-api';
import {
  CASCADE_QUERY_PREFIXES,
  PROD_JUDGMENT_KEY,
  PROD_STATE_KEY,
  SYNC_STATUS_KEY,
  fetchProdState,
  useSyncStatus,
} from '@/lib/promotion-api';
import { PROTOCOL_WORDING } from '@/lib/protocol-wording';
import { RULE_SETTINGS_WORDING } from '@/lib/rule-settings-wording';
import { useInstanceContext } from '@/lib/use-instance';
import { useNow } from '@/lib/use-now';
import type { VersionedWording } from '@/lib/versioned-labels';
import type {
  ProdStateAnomaly,
  ProdStateCandidate,
  PromotableKind,
  PromotionBox,
} from '@matter_extractor/shared';
import { Badge, type BadgeTone, Button } from '@pms-digital/design-system';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';

/**
 * « État de la prod » (docs/specs/vitrine.md §9.3, issue #192, lot F; maquette `atelier.html`
 * section 2): what the dev has ahead of the prod, object by object, and what the prod says of each.
 *
 * ## What it asks, and in which order
 *
 * 1. `GET /api/atelier/sync` - the state, under the key the banner shares. Without a prod linked
 *    (`configured` false) the screen says so and draws no group: there is no state of the prod to show.
 * 2. `GET /api/atelier/prod-state` - the LOCAL half: the entities whose « latest dev » is ahead of
 *    « latest prod », and the anomalies. It never calls the prod.
 * 3. One `POST /api/atelier/promotion/check` per candidate, all at once (`useQueries`): the prod
 *    judges each line at blank, with its own code, when the screen opens. The key is the screen's own
 *    (`PROD_JUDGMENT_KEY`), and the queries are KEPT: when the prod stops answering, the last verdict
 *    a line got is still what is shown.
 *
 * ## The prod that does not answer
 *
 * A box in `state: 'unreachable'` is made an ERROR of its query (`judge` below), so the cache keeps
 * the previous verdict instead of overwriting it with « unreachable ». The screen then says it in the
 * red note of the maquette - the same note when the state of the synchronisation is in failure - and
 * EVERY button is inert. A line the prod never judged stays in « Prêt à passer », with a grey badge
 * and an inert button: it was not refused, and it cannot go.
 *
 * « Passer en production » opens the box of the cascade the editors open (`CascadeDialog`), with the
 * wording of the kind; when it went through, every family the cascade can move is read again.
 */

/** What the box of the cascade needs to word a kind: its test-id prefix and its subject. */
type CascadeWording = Pick<VersionedWording, 'entity' | 'subject'>;

const WORDINGS: Record<PromotableKind, CascadeWording> = {
  criterion: CRITERION_WORDING,
  persona: PERSONA_WORDING,
  protocol: PROTOCOL_WORDING,
  experience: EXPERIENCE_WORDING,
  'noyau-texts': NOYAU_TEXTS_WORDING,
  models: MODEL_SETTINGS_WORDING,
  rules: RULE_SETTINGS_WORDING,
  method: METHOD_WORDING,
};

/** A judgment the prod did not render: the box says `unreachable`, which is a state and not an answer. */
class ProdSilent extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ProdSilent';
  }
}

/**
 * At most this many judgments in flight: each one is a round trip from the atelier to the prod, and
 * the first opening after a long gap can list every entity the prod never received. The others wait
 * their turn, in the order of the screen.
 */
const JUDGMENTS_AT_ONCE = 4;
let judging = 0;
const waitingTurn: (() => void)[] = [];

async function inTurn<T>(work: () => Promise<T>): Promise<T> {
  if (judging < JUDGMENTS_AT_ONCE) judging += 1;
  else await new Promise<void>((resolve) => waitingTurn.push(resolve));
  try {
    return await work();
  } finally {
    // The slot is handed to the next in line as it is, or given back.
    const next = waitingTurn.shift();
    if (next === undefined) judging -= 1;
    else next();
  }
}

async function judge(candidate: ProdStateCandidate): Promise<PromotionBox> {
  const box = await inTurn(() =>
    checkPromotion({
      type: 'promote',
      kind: candidate.kind,
      entityId: candidate.entityId,
      versionNumber: candidate.development.number,
    }),
  );
  if (box.state === 'unreachable') throw new ProdSilent(box.message ?? 'La prod ne répond pas.');
  return box;
}

/** The verdict's pill, a package `Badge` in the tone of the maquette's colour. */
const BADGE_TONE: Record<VerdictBadge, BadgeTone> = {
  ok: 'success',
  refus: 'danger',
  attente: 'warning',
  'pas-jugee': 'neutral',
  jugement: 'info',
};

function EntityName({
  kind,
  entityId,
  name,
}: { kind: PromotableKind; entityId: string; name: string }) {
  const target = editorTarget(kind, entityId);
  return (
    <Link
      {...target}
      className="font-bold text-bleu-nuit underline-offset-2 hover:underline"
      data-testid="etat-prod-nom"
    >
      {name}
    </Link>
  );
}

function Eyebrow({ kind }: { kind: PromotableKind }) {
  return (
    <span className="font-bold text-gray-500 text-micro uppercase tracking-[0.05em]">
      {KIND_EYEBROW[kind]}
    </span>
  );
}

function GroupTitle({ testId, title, count }: { testId: string; title: string; count: number }) {
  return (
    <h3
      data-testid={testId}
      className="mt-1 flex items-baseline gap-2 font-bold text-bleu-nuit text-body"
    >
      {title}
      <span className="rounded-full bg-gray-100 px-2 font-bold text-caption text-gray-600">
        {count}
      </span>
    </h3>
  );
}

function Empty({ children, testId }: { children: string; testId: string }) {
  return (
    <p
      data-testid={testId}
      className="rounded-lg border border-gray-300 border-dashed px-3 py-2.5 text-gray-600 text-small"
    >
      {children}
    </p>
  );
}

type Line = { candidate: ProdStateCandidate; verdict: Verdict };

function CandidateRow({
  line,
  inert,
  onPass,
}: { line: Line; inert: boolean; onPass: (candidate: ProdStateCandidate) => void }) {
  const { candidate, verdict } = line;
  return (
    <li
      data-testid="etat-prod-ligne"
      data-verdict={verdict.badge}
      className="grid grid-cols-1 items-center gap-x-3 gap-y-1 border-gray-100 border-b px-3 py-2.5 last:border-b-0 min-[601px]:grid-cols-[minmax(0,1fr)_auto]"
    >
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <Eyebrow kind={candidate.kind} />
        <EntityName kind={candidate.kind} entityId={candidate.entityId} name={candidate.name} />
        <b className="font-bold">v{candidate.development.number}</b>
        <span className="text-caption text-gray-600">{productionNote(candidate.production)}</span>
      </div>
      <p className="flex flex-wrap items-baseline gap-x-1.5 gap-y-1 text-caption text-gray-700">
        <span data-testid="etat-prod-pastille" className="inline-flex">
          <Badge tone={BADGE_TONE[verdict.badge]} maxWidth="none">
            {verdict.badgeText}
          </Badge>
        </span>
        <span data-testid="etat-prod-verdict">{verdict.text}</span>
      </p>
      <div className="flex flex-col items-start min-[601px]:col-start-2 min-[601px]:row-span-2 min-[601px]:row-start-1 min-[601px]:items-end">
        <Button
          type="button"
          size="sm"
          variant="accent"
          data-testid="etat-prod-passer"
          disabled={inert || !verdict.passable}
          onClick={() => onPass(candidate)}
        >
          Passer en production
        </Button>
      </div>
    </li>
  );
}

function AnomalyRow({ anomaly }: { anomaly: ProdStateAnomaly }) {
  return (
    <li
      data-testid="etat-prod-anomalie"
      className="space-y-1.5 border-gray-100 border-b px-3 py-2.5 last:border-b-0"
    >
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <Eyebrow kind={anomaly.kind} />
        {anomaly.entityName === null ? (
          <b className="font-bold text-gray-600">entité retirée</b>
        ) : (
          <EntityName kind={anomaly.kind} entityId={anomaly.entityId} name={anomaly.entityName} />
        )}
        <b className="font-bold">v{anomaly.versionNumber}</b>
        <Badge tone="danger" maxWidth="none">
          {anomalyReason(anomaly.reason)}
        </Badge>
      </div>
      <p className="text-caption text-gray-700">
        détectée le {dayAndHour(anomaly.detectedAt)}, remontée par la session{' '}
        <Link
          to="/admin/sessions/$sessionId"
          params={{ sessionId: anomaly.sessionId }}
          className="font-semibold text-bleu-nuit underline-offset-2 hover:underline"
        >
          {anomaly.sessionId.slice(0, 8)}
        </Link>
      </p>
      <details data-testid="etat-prod-anomalie-contenus">
        <summary className="cursor-pointer font-semibold text-bleu-nuit text-caption">
          Voir les deux contenus
        </summary>
        <div className="mt-2 grid grid-cols-1 gap-2 min-[601px]:grid-cols-2">
          <div className="min-w-0 space-y-1">
            <p className="font-semibold text-caption text-gray-700">Contenu du dev</p>
            <pre
              data-testid="etat-prod-contenu-dev"
              className="max-h-72 overflow-auto rounded-md bg-gray-50 p-2 text-caption text-gray-800"
            >
              {prettyContent(anomaly.localContent)}
            </pre>
          </div>
          <div className="min-w-0 space-y-1">
            <p className="font-semibold text-caption text-gray-700">Contenu de la prod</p>
            <pre
              data-testid="etat-prod-contenu-prod"
              className="max-h-72 overflow-auto rounded-md bg-gray-50 p-2 text-caption text-gray-800"
            >
              {prettyContent(anomaly.prodContent)}
            </pre>
          </div>
        </div>
      </details>
    </li>
  );
}

function Box({
  label,
  testId,
  children,
}: { label: string; testId: string; children: React.ReactNode }) {
  return (
    <div
      data-testid={testId}
      className="flex min-w-0 flex-col rounded-lg border border-gray-200 px-2.5 py-2 text-caption text-gray-600"
    >
      <span>{label}</span>
      {children}
    </div>
  );
}

export function ProdStateScreen() {
  const { code: devCode } = useInstanceContext();
  const queryClient = useQueryClient();
  const now = useNow();
  const status = useSyncStatus(true);
  const linked = status.data?.configured === true;
  const [open, setOpen] = useState<ProdStateCandidate | null>(null);

  const state = useQuery({
    queryKey: PROD_STATE_KEY,
    queryFn: fetchProdState,
    enabled: linked,
    retry: false,
  });
  const candidates = state.data?.candidates ?? [];
  const judgments = useQueries({
    queries: candidates.map((candidate) => ({
      queryKey: [
        PROD_JUDGMENT_KEY,
        candidate.kind,
        candidate.entityId,
        candidate.development.number,
      ],
      queryFn: () => judge(candidate),
      retry: false,
      staleTime: 0,
      refetchOnWindowFocus: false,
    })),
  });

  if (status.isPending) {
    return (
      <section data-testid="etat-prod" className="space-y-4">
        <h1 className="font-bold text-bleu-nuit text-h2">{SCREEN_TITLE}</h1>
        <output className="block text-gray-600 text-small">Chargement…</output>
      </section>
    );
  }

  const title = (
    <header className="space-y-1">
      <h1 className="font-bold text-bleu-nuit text-h2">{SCREEN_TITLE}</h1>
      <p data-testid="etat-prod-intro" className="max-w-[92ch] text-gray-700 text-small">
        {SCREEN_LEAD}
      </p>
    </header>
  );

  if (status.data === undefined || !status.data.configured) {
    return (
      <section data-testid="etat-prod" className="space-y-4">
        {title}
        {status.data === undefined ? (
          <p
            data-testid="etat-prod-erreur"
            className="rounded-md bg-rouge-tint px-2.5 py-2 text-rouge-deep text-small"
          >
            L’état de la synchronisation n’a pas pu être lu.
          </p>
        ) : (
          <p
            data-testid="etat-prod-sans-prod"
            className="rounded-lg border border-gray-300 border-dashed px-3 py-2.5 text-gray-700 text-small"
          >
            {NO_PROD_SENTENCE}
          </p>
        )}
      </section>
    );
  }

  const sync = status.data;
  const lines: Line[] = candidates.map((candidate, index) => {
    const judgment = judgments[index];
    return {
      candidate,
      verdict: verdictOf(judgment?.data ?? null, judgment?.isPending ?? true),
    };
  });
  const ready = lines.filter((line) => line.verdict.group === 'ready');
  const waiting = lines.filter((line) => line.verdict.group === 'waiting');

  // The prod is silent when a judgment came back so, or when the last synchronisation failed.
  const silentSince = [
    ...(isFailing(sync) && sync.lastErrorAt !== null ? [Date.parse(sync.lastErrorAt)] : []),
    ...judgments.filter((judgment) => judgment.isError).map((judgment) => judgment.errorUpdatedAt),
  ].filter((stamp) => !Number.isNaN(stamp));
  const silent = silentSince.length > 0;
  const since = silent ? clockTime(new Date(Math.min(...silentSince)).toISOString()) : null;

  const box = syncBox(sync, now);
  const compare = devCompareNote(sync.prod?.code ?? null, devCode);

  const reread = () => {
    void queryClient.invalidateQueries({ queryKey: [PROD_JUDGMENT_KEY] });
    void queryClient.invalidateQueries({ queryKey: PROD_STATE_KEY });
    void queryClient.invalidateQueries({ queryKey: SYNC_STATUS_KEY });
  };

  return (
    <section data-testid="etat-prod" className="space-y-4">
      {title}

      <div className="grid grid-cols-[repeat(auto-fit,minmax(170px,1fr))] gap-2">
        <Box label="Code de la prod" testId="etat-prod-code-prod">
          <b className="font-bold text-body text-gray-900">{sync.prod?.code ?? 'inconnu'}</b>
          <span>{prodReadNote(sync)}</span>
        </Box>
        <Box label="Code du dev" testId="etat-prod-code-dev">
          <b className="font-bold text-body text-gray-900">{devCode}</b>
          {'href' in compare ? (
            <a
              data-testid="etat-prod-avance"
              href={compare.href}
              target="_blank"
              rel="noreferrer noopener"
              className="font-semibold text-bleu-nuit underline underline-offset-2"
            >
              voir l’avance sur GitLab
            </a>
          ) : (
            <span>{compare.text}</span>
          )}
        </Box>
        <Box label="Synchronisation" testId="etat-prod-sync">
          <b className={`font-bold text-body ${box.bad ? 'text-rouge-deep' : 'text-gray-900'}`}>
            {box.text}
          </b>
          <span>une fois par heure, et au clic</span>
        </Box>
      </div>

      {silent ? (
        <div
          role="alert"
          data-testid="etat-prod-injoignable"
          className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-rouge-tint px-2.5 py-2 text-rouge-deep text-small"
        >
          <span>{unreachableNote(since)}</span>
          <Button
            type="button"
            size="sm"
            variant="accent"
            data-testid="etat-prod-reessayer"
            onClick={reread}
          >
            Juger de nouveau
          </Button>
        </div>
      ) : null}

      {state.isError ? (
        <p
          data-testid="etat-prod-erreur"
          className="rounded-md bg-rouge-tint px-2.5 py-2 text-rouge-deep text-small"
        >
          La liste des objets en avance n’a pas pu être lue.
        </p>
      ) : null}

      <GroupTitle testId="etat-prod-groupe-pret" title="Prêt à passer" count={ready.length} />
      {ready.length === 0 ? (
        <Empty testId="etat-prod-vide-pret">
          Rien à passer : latest prod est à jour pour tous les objets.
        </Empty>
      ) : (
        <ul data-testid="etat-prod-liste-pret" className="rounded-lg border border-gray-200">
          {ready.map((line) => (
            <CandidateRow
              key={`${line.candidate.kind}-${line.candidate.entityId}`}
              line={line}
              inert={silent}
              onPass={setOpen}
            />
          ))}
        </ul>
      )}

      <GroupTitle testId="etat-prod-groupe-attente" title="Attend un tag" count={waiting.length} />
      {waiting.length === 0 ? (
        <Empty testId="etat-prod-vide-attente">
          Rien n’attend un tag : la prod accepte tout ce qui est prêt.
        </Empty>
      ) : (
        <ul data-testid="etat-prod-liste-attente" className="rounded-lg border border-gray-200">
          {waiting.map((line) => (
            <CandidateRow
              key={`${line.candidate.kind}-${line.candidate.entityId}`}
              line={line}
              inert={silent}
              onPass={setOpen}
            />
          ))}
        </ul>
      )}

      <GroupTitle
        testId="etat-prod-groupe-anomalies"
        title="Anomalies de synchronisation"
        count={state.data?.anomalies.length ?? 0}
      />
      {(state.data?.anomalies.length ?? 0) === 0 ? (
        <Empty testId="etat-prod-vide-anomalies">{EMPTY_ANOMALIES}</Empty>
      ) : (
        <ul data-testid="etat-prod-liste-anomalies" className="rounded-lg border border-gray-200">
          {(state.data?.anomalies ?? []).map((anomaly) => (
            <AnomalyRow key={anomaly.id} anomaly={anomaly} />
          ))}
        </ul>
      )}

      {open === null ? null : (
        <CascadeDialog
          wording={WORDINGS[open.kind]}
          name={open.name}
          gesture={{
            type: 'promote',
            kind: open.kind,
            entityId: open.entityId,
            versionNumber: open.development.number,
          }}
          target={open.development}
          current={open.production}
          onPassed={() => {
            // The cascade moved other entities too (vitrine.md §4.2): this screen, the state and
            // every family a cascade can touch are read again.
            reread();
            for (const prefix of CASCADE_QUERY_PREFIXES) {
              void queryClient.invalidateQueries({ queryKey: [prefix] });
            }
          }}
          onCancel={() => setOpen(null)}
        />
      )}
    </section>
  );
}
